'use client'

import { createContext, useContext, useEffect, useState, useMemo, useRef, useCallback } from 'react'
import posthog from 'posthog-js'
import { createClient, type AuthUser } from '@/lib/supabase/client'
import { signOutOrThrow } from '@/lib/auth/sign-out'
import { clearRecoveryMarker, setRecoveryMarker } from '@/lib/auth/recovery-marker'
import { type AuthChangeEvent, type Session } from '@supabase/supabase-js'

type AuthContextType = {
  user: AuthUser | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const lastIdentifiedUserId = useRef<string | null>(null)
  const hasResetIdentityForNoSession = useRef(false)

  // Memoize the supabase client instance to prevent infinite re-initializations
  const supabase = useMemo(() => createClient(), [])

  const resetIdentityIfNoSession = useCallback(() => {
    if (hasResetIdentityForNoSession.current) return

    posthog.reset()
    lastIdentifiedUserId.current = null
    hasResetIdentityForNoSession.current = true
  }, [])

  const identifyAuthenticatedUser = useCallback((authUser: AuthUser | null) => {
    if (!authUser) {
      resetIdentityIfNoSession()
      return
    }

    hasResetIdentityForNoSession.current = false
    if (lastIdentifiedUserId.current === authUser.id) return

    posthog.identify(authUser.id, {
      email: authUser.email,
      auth_provider: authUser.app_metadata?.provider,
    })

    lastIdentifiedUserId.current = authUser.id
  }, [resetIdentityIfNoSession])

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const authUser = session?.user || null
      if (!authUser) {
        clearRecoveryMarker()
      }
      setUser(authUser)
      identifyAuthenticatedUser(authUser)
      setLoading(false)
    }

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: AuthChangeEvent, session: Session | null) => {
        const authUser = session?.user || null
        if (event === 'PASSWORD_RECOVERY' && authUser) {
          setRecoveryMarker({
            userId: authUser.id,
            verifiedAtMs: Date.now(),
          })
        } else if (!authUser) {
          clearRecoveryMarker()
        }
        setUser(authUser)
        identifyAuthenticatedUser(authUser)
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [supabase, identifyAuthenticatedUser])

  const signOut = async () => {
    await signOutOrThrow(supabase)
  }

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
